/**
 * #2102 — attachments in the panel: what may be picked, and how a stored file
 * becomes something a browser can show.
 *
 * Pure, and separate from the component for the usual reason: the component
 * cannot be loaded under node, and the mistakes in this area are silent —
 * a refused file with no explanation, an object URL leaked for the life of the
 * tab, or a five-minute pre-signed URL cached until it 403s.
 *
 * ══ THE SERVER IS THE AUTHORITY ON WHAT MAY BE ATTACHED ══
 *
 * cs3-chat #25 sniffs the CONTENT of every upload and refuses anything that is
 * not JPEG, PNG, HEIC or PDF, over 10 MB, or posted when the posting rules say
 * no. The checks here are a COURTESY: they stop a 40 MB video being uploaded
 * over a phone connection only to be refused, and they put the reason on screen
 * before the round trip rather than after it.
 *
 * ⚠ SO THEY MUST NEVER BE MORE PERMISSIVE THAN THE SERVER, and they must never
 * be treated as the gate. The browser cannot sniff content reliably — a
 * File's `type` is the operating system's guess from its extension — so a file
 * that passes here can still be refused on its bytes, and the panel has to show
 * that refusal properly. Both paths are tested.
 */

/*
 * ⚠ `normaliseAttachments` IS IN `chatShapes.js`, NOT HERE. It shapes a
 * FIRESTORE DOCUMENT's field, which is what that module is for, and
 * `chatShapes.js` deliberately imports nothing so every node test can stage it
 * as a single file. This module holds the panel's own concerns: what may be
 * picked, what a refusal says, and the fetched-file cache.
 */

/** The media types cs3-chat accepts (`AttachmentType::accepted()`). */
export const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'application/pdf'];

/**
 * What to put in the file input's `accept`.
 *
 * ⚠ EXTENSIONS AS WELL AS MEDIA TYPES. iOS and some Android builds report a
 * HEIC photo as `image/heif`, as an empty string, or as
 * `application/octet-stream` — so a media-type-only `accept` greys out the very
 * photos an iPhone takes. The extensions make the picker show them; the server
 * still decides on the bytes.
 */
export const ACCEPT_ATTR = [
    'image/jpeg', 'image/png', 'image/heic', 'image/heif', 'application/pdf',
    '.jpg', '.jpeg', '.png', '.heic', '.heif', '.pdf',
].join(',');

/** `AttachmentRules::MAX_BYTES` — 10 MB, binary, as a file manager shows it. */
export const MAX_BYTES = 10 * 1024 * 1024;

/** How many files one message may carry (the messages endpoint's own cap). */
export const MAX_PER_MESSAGE = 10;

/**
 * How long a fetched file is reused before the panel asks for a fresh link.
 *
 * ⚠ SHORTER THAN THE SERVER'S FIVE MINUTES (`chat.attachments.link_ttl`), and
 * it has to be: the pre-signed URL is a bearer token with a hard expiry, so a
 * fetch started at 4 minutes 59 seconds can arrive after it has lapsed. Four
 * minutes leaves a margin and still means one request per file per sitting.
 */
export const REUSE_MS = 4 * 60 * 1000;

/**
 * Extensions whose media type the OS commonly gets wrong.
 *
 * Used only to recognise a HEIC that arrived with no type at all, so the panel
 * can say "that is a photo and the server will take it" rather than refusing
 * the one format an iPhone defaults to.
 */
const HEIC_EXTENSIONS = ['.heic', '.heif'];

/**
 * May this file be offered to the server?
 *
 * Returns null when it may, or a refusal code the panel has a sentence for.
 * The codes match cs3-chat's `AttachmentRefusal` values so that a refusal
 * decided here and the same refusal coming back from the server render
 * identically — one sentence per reason, wherever the reason was noticed.
 *
 * ⚠ SIZE BEFORE TYPE, as the server does it. Telling someone "we only accept
 * JPEG, PNG, HEIC and PDF" about a perfectly good 40 MB photo sends them off to
 * convert a file that was never the problem.
 */
export function refusalFor(file) {
    if (!file) return 'attachment_empty';

    const size = Number(file.size);
    if (!isFinite(size) || size <= 0) return 'attachment_empty';
    if (size > MAX_BYTES) return 'attachment_too_large';

    /*
     * ⚠ A BROWSER'S `type` IS A GUESS FROM THE EXTENSION, and an empty string
     * is common — Android for HEIC, some Windows builds for PDF. So an unknown
     * type is NOT refused here: it is sent, and the server decides on the
     * bytes. Only a type we positively recognise as wrong (a video, a zip) is
     * stopped early, which is the case this check exists for.
     */
    const type = String(file.type || '').toLowerCase();
    if (type === '' || looksLikeHeic(file)) return null;

    return ACCEPTED_TYPES.indexOf(type) >= 0 ? null : 'attachment_wrong_type';
}

function looksLikeHeic(file) {
    const name = String((file && file.name) || '').toLowerCase();

    return HEIC_EXTENSIONS.some((ext) => name.endsWith(ext));
}

/**
 * The sentence for a refusal, wherever it came from.
 *
 * The keys are cs3-chat's `AttachmentRefusal` values plus the posting refusals
 * (`PostRefusal`), because the upload endpoint answers with those too — an
 * attachment is part of a message, so office hours refuse it exactly as they
 * refuse the message.
 */
export function refusalText(code) {
    return {
        attachment_empty: 'That file is empty.',
        attachment_too_large: 'Attachments are up to 10 MB each.',
        attachment_wrong_type: 'Attachments can be JPEG, PNG or HEIC photos, or a PDF.',
        attachments_not_configured: 'Attachments are not set up on this server yet.',
        attachment_store_failed: 'The attachment could not be stored. Try again in a moment.',
        // The posting rules, which refuse an upload as they refuse a message.
        not_permitted: 'You cannot post in this channel.',
        archived: 'This channel is archived. It is read-only.',
        group_off: 'This channel group is switched off for your client.',
        own_option_off: 'Chat is not switched on for this job or employer.',
        outside_job_window: 'This job channel is open from 24 hours before the job starts '
            + 'until 24 hours after it ends.',
        outside_office_hours: 'Chat is read-only outside your office hours.',
        direct_unreachable: 'This conversation is read-only: you no longer share a channel with this person.',
        chat_session_expired: 'Your chat session has expired. Reload the page.',
    }[code] || 'That attachment could not be sent. Try again in a moment.';
}

/**
 * #2103 — the image files in a paste, if there are any.
 *
 * Ed pasted a Windows Snipping Tool capture into the chat box and nothing
 * happened: the composer only accepted the paperclip and drag-and-drop.
 *
 * ⚠ A SNIP ARRIVES AS A FILE WITH NO USEFUL NAME. `clipboardData.items` carries
 * it as `kind: 'file'`, `type: 'image/png'`, and `getAsFile()` returns a File
 * whose `name` is `image.png` or empty depending on the browser — so the name
 * is OURS to invent, and it has to be unique or two pastes in one message are
 * indistinguishable in the queue.
 *
 * ⚠ AND A PASTE IS USUALLY TEXT, which must go on working exactly as it does
 * today (issue item 6). So this returns an empty list for a text paste and the
 * caller leaves the event alone — `preventDefault` is only correct when there
 * really is an image, and calling it unconditionally would stop people pasting
 * a gate code into chat.
 *
 * @param {DataTransfer} clipboardData
 * @param {function(): number} now  injected for tests
 * @returns {Array<File>} the images, renamed
 */
export function imagesFromPaste(clipboardData, now) {
    const dt = clipboardData;
    if (!dt) return [];

    const stamp = (now || (() => Date.now()))();

    /*
     * ══ ⚠ ONE SOURCE, NOT BOTH. Ed: "the cut and paste is pasting duplicates" ══
     *
     * The first version read `items` AND `files` and de-duplicated on
     * `type:size:lastModified`. That does not work: for one clipboard image,
     * `item.getAsFile()` and the `dt.files` entry are DIFFERENT File objects and
     * each carries its own `lastModified` — the moment it was constructed — so
     * the keys differ and the same snip was added twice. Reported on stage- in
     * **Chrome and not Firefox**, which fits exactly: Chrome populates both,
     * Firefox only `items`.
     *
     * ⚠ AND `files` CAN NEVER CONTAIN AN IMAGE `items` DOES NOT. The HTML spec
     * defines `DataTransfer.files` as a view DERIVED from the items of kind
     * `file` — so reading it after `items` can only ever add duplicates, never
     * find anything new. That is what makes "items, then files only if items
     * gave nothing" the structural fix rather than a heuristic: the fallback
     * exists solely for a browser whose `items` is empty or unusable.
     *
     * ⚠ AND THE DEDUPE IS NOT type+size EITHER. It was the other option offered,
     * and it trades this bug for a quieter one: two genuinely different snips of
     * the same byte length — easy enough with two captures of the same region —
     * would silently become one. With a single source there is nothing to
     * de-duplicate, so there is no key to get wrong.
     */
    const fromItems = imageFiles(dt.items ? [...dt.items] : [], (item) => (
        item && item.kind === 'file' && typeof item.getAsFile === 'function'
            ? item.getAsFile()
            : null
    ));

    const found = fromItems.length > 0
        ? fromItems
        : imageFiles(dt.files ? [...dt.files] : [], (file) => file);

    return found.map((file, index) => renamePasted(file, String(file.type).toLowerCase(), stamp, index));
}

/**
 * The image files in one clipboard source.
 *
 * ⚠ IMAGES ONLY. A pasted PDF arrives as a file with no bytes in some browsers
 * and a pasted Word selection arrives as `text/html` — neither is what Ed asked
 * for, and the paperclip handles a real PDF.
 *
 * @param {Array} source     `items` or `files`
 * @param {function} toFile  how to get a File out of one entry
 * @returns {Array<File>}
 */
function imageFiles(source, toFile) {
    const out = [];

    source.forEach((entry) => {
        const file = toFile(entry);
        if (!file) return;
        if (String(file.type || '').toLowerCase().indexOf('image/') !== 0) return;
        out.push(file);
    });

    return out;
}

/**
 * What to call a pasted image.
 *
 * Exported and pure because it is the part with a RULE in it: the name has to
 * be unique within a paste, or two snips in one message are indistinguishable
 * in the queue and in the conversation. `new File` below is a browser
 * capability, not logic, and is tested as such.
 */
export function pastedName(type, stamp, index) {
    const ext = {
        'image/png': 'png', 'image/jpeg': 'jpg', 'image/heic': 'heic', 'image/webp': 'webp',
    }[String(type || '').toLowerCase()] || 'png';

    return 'pasted-' + stamp + (index > 0 ? '-' + (index + 1) : '') + '.' + ext;
}

/**
 * A pasted image, with a name we chose.
 *
 * ⚠ A `File` IS READ-ONLY, so `file.name = …` silently does nothing in some
 * browsers and throws in others. A new `File` built from the same blob is the
 * only way to rename one.
 *
 * ⚠ AND THE FALLBACK IS HARMLESS BUT WORTH KNOWING. Where the `File`
 * constructor is missing (older Safari, and node, which is why the tests say
 * so) the original is returned: the upload still works, because
 * `uploadAttachment` passes the name to `FormData.append` as its third
 * argument, and the server sanitises whatever arrives — a snip then shows as
 * `image.png`, or as `attachment.png` if the clipboard gave no name at all.
 * Two pastes in one message would share a name, which is cosmetic.
 */
function renamePasted(file, type, stamp, index) {
    const name = pastedName(type, stamp, index);

    try {
        return new File([file], name, { type: file.type, lastModified: file.lastModified || stamp });
    } catch (e) {
        return file;
    }
}

/** "2.4 MB" — the size beside a chip. */
export function sizeLabel(bytes) {
    const n = Number(bytes) || 0;
    if (n >= 1024 * 1024) return (Math.round(n / 1024 / 1024 * 10) / 10) + ' MB';
    if (n >= 1024) return Math.round(n / 1024) + ' KB';

    return n + ' B';
}

/** What to call a file in a chip: the name, and the kind if the name has none. */
export function kindLabel(type) {
    return {
        'image/jpeg': 'JPEG image',
        'image/png': 'PNG image',
        'image/heic': 'HEIC photo',
        'application/pdf': 'PDF document',
    }[type] || 'File';
}

/**
 * A cache of fetched files, keyed by attachment id.
 *
 * ══ ⚠ WHY THE BYTES ARE CACHED AND THE URL IS NOT ══
 *
 * The pre-signed URL expires in five minutes and cannot be renewed, so caching
 * it would give a thumbnail that works for five minutes and 403s afterwards —
 * which is exactly the bug the handoff warns about ("don't cache the URL").
 * What CAN be kept is the object URL made from the bytes, which never expires.
 *
 * ⚠ AND EVERY OBJECT URL MUST BE REVOKED. `URL.createObjectURL` pins the blob
 * in memory until `revokeObjectURL` is called or the document goes — so a panel
 * that fetched fifty photos over a shift would hold fifty blobs for the life of
 * the tab. `release()` is called from the component's `beforeDestroy`, and the
 * cache evicts its oldest entries past `limit`.
 *
 * Takes its globals so it runs under node against fakes.
 */
export function createFileCache(opts) {
    const o = opts || {};

    /*
     * ⚠ AN EXPLICIT `null` MEANS "THERE IS NONE", not "use the global".
     *
     * A plain `o.createObjectURL || URL.createObjectURL` cannot tell an absent
     * option from one deliberately set to null, so a caller could not describe a
     * browser without object URLs — and a test that tried would silently
     * exercise the real `URL`, which is how this was found. Absent falls back to
     * the global; anything that is not a function is none.
     */
    const pick = (given, fallback) => {
        if (given === undefined) return fallback;

        return typeof given === 'function' ? given : null;
    };

    const globalUrl = (typeof URL !== 'undefined') ? URL : null;
    const createUrl = pick(o.createObjectURL,
        globalUrl && globalUrl.createObjectURL ? globalUrl.createObjectURL.bind(globalUrl) : null);
    const revokeUrl = pick(o.revokeObjectURL,
        globalUrl && globalUrl.revokeObjectURL ? globalUrl.revokeObjectURL.bind(globalUrl) : null);
    const now = pick(o.now, () => Date.now()) || (() => Date.now());
    const limit = o.limit || 24;

    // id -> { url, at }
    const entries = new Map();

    function drop(id) {
        const hit = entries.get(id);
        if (!hit) return;
        entries.delete(id);
        try { if (revokeUrl) revokeUrl(hit.url); } catch (e) { /* already gone */ }
    }

    function evict() {
        // Oldest first. Map preserves insertion order, and `put` re-inserts, so
        // the first key is the least recently fetched.
        while (entries.size > limit) {
            const oldest = entries.keys().next();
            if (oldest.done) return;
            drop(oldest.value);
        }
    }

    return {
        /** A usable object URL for this attachment, or null when none is held. */
        get(id) {
            const hit = entries.get(id);
            if (!hit) return null;

            // Past the reuse window: drop it, so the next view fetches a fresh
            // link. The blob itself is still valid — this is about the file
            // having possibly changed hands, not about the URL expiring.
            if (now() - hit.at > REUSE_MS) {
                drop(id);

                return null;
            }

            return hit.url;
        },

        /** Keep the bytes, and hand back the object URL to render. */
        put(id, blob) {
            if (!createUrl) return null;

            drop(id);
            const url = createUrl(blob);
            entries.set(id, { url, at: now() });
            evict();

            return url;
        },

        has(id) { return entries.has(id); },

        size() { return entries.size; },

        /** Revoke everything. Called when the panel is destroyed. */
        release() {
            [...entries.keys()].forEach((id) => drop(id));
        },
    };
}
